import { SEARCH_KIND } from "@/constants";
import { ROUTES } from "@/routes_new";
import { Search } from "@/types/Search";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { copyToClipboard, formatDateForApi, slugify } from "@/utils";
import { Button } from "@components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from "@components/ui/item";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import {
  ChartColumnBigIcon,
  ExternalLinkIcon,
  MessageSquareIcon,
} from "lucide-react";
import stringify from "query-string";
import { useState } from "react";
import { generatePath } from "react-router-dom";
import { toast } from "sonner";
import { Field, FieldLabel } from "@components/ui/field";
import { Checkbox } from "@components/ui/checkbox";
import { subMonths } from "date-fns";
import { DialogProps } from "@radix-ui/react-dialog";
import { useI18n } from "@/contexts/i18nContext";
import { Feature } from "@components/Feature";
import { FEATURE } from "@/features";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import { useStringifySearchFilters } from "@/hooks/useStringifySearchFilters";

export default function SearchShareDialog({
  search,
  filters,
  ...rest
}: {
  search: Nullable<Search> | undefined;
  filters: SearchFilterV4;
} & DialogProps) {
  const { language } = useI18n();
  const { t } = useLingui();

  // Local state
  const [shouldShowKpis, setshouldShowKpis] = useState(true);

  // Helpers
  const searchTitle = search?.title;

  const jsonUrl =
    search &&
    generatePath(ROUTES.SEARCH_EXPORT_API, {
      search_uuid: search.uuid,
    });

  const rssUrl =
    search &&
    generatePath(ROUTES.SEARCH_EXPORT_RSS, {
      search_uuid: search.uuid,
      language,
    });

  const stringifiedFilters = useStringifySearchFilters(search, filters);

  const isShareable =
    search?.kind === SEARCH_KIND.SAVED || search?.kind === SEARCH_KIND.REPORT;

  const shareUrl = isShareable
    ? stringify.stringifyUrl({
        url:
          search?.uuid &&
          `${window.location.origin}${generatePath(ROUTES.ALERT_PUBLIC, {
            uuid: search.uuid,
            slug: slugify(search.title),
            language,
          })}?${stringifiedFilters}`,
        query: {
          kpis: shouldShowKpis,
          sentiment: true,
        },
      })
    : null;

  const insightsShareUrl = search?.insights_report_uuid
    ? stringify.stringifyUrl(
        {
          url: `${window.location.origin}${generatePath(
            ROUTES.MENTION_INSIGHTS_SHARE,
            {
              report_uuid: search.insights_report_uuid,
            }
          )}`,
          query: {
            ...filters,
            start_date:
              filters.start_date || formatDateForApi(subMonths(new Date(), 3)),
            end_date: filters.end_date || formatDateForApi(new Date()),
          },
        },
        { arrayFormat: "comma" }
      )
    : undefined;

  // Handlers
  async function handleCopyMentionsUrl() {
    postTrackingData(EVENT.SHARE_ALERT_COPIED, {
      shareUrl: shareUrl,
      uuid: search?.uuid,
    });

    try {
      if (!shareUrl) {
        throw new Error();
      }

      await copyToClipboard(shareUrl);

      toast.success(t`Länken kopierades`);
    } catch {
      toast.error(t`Länken kunde inte kopieras`);
    }
  }

  async function handleCopyInsightsUrl() {
    postTrackingData(EVENT.SHARE_ALERT_ANALYTICS_COPIED, {
      shareUrl: insightsShareUrl,
      uuid: search?.uuid,
    });

    try {
      if (!insightsShareUrl) {
        throw new Error();
      }

      await copyToClipboard(insightsShareUrl);

      toast.success(t`Länken kopierades`);
    } catch {
      toast.error(t`Länken kunde inte kopieras`);
    }
  }

  return (
    <Dialog {...rest}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Dela {searchTitle}</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Offentliga länkar kan ses av alla. Kopiera en länk för att dela.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Item variant="outline" size="sm">
            <ItemMedia variant="icon">
              <MessageSquareIcon />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>
                <Trans>Publik länk för omnämnanden</Trans>
              </ItemTitle>
              <Field orientation="horizontal">
                <Checkbox
                  id="show-kpis"
                  checked={shouldShowKpis}
                  onCheckedChange={(newChecked) => {
                    setshouldShowKpis(newChecked === true);
                  }}
                />

                <FieldLabel htmlFor="show-kpis">
                  <Trans>Visa PR-värde och räckvidd</Trans>
                </FieldLabel>
              </Field>
            </ItemContent>
            <ItemActions>
              {shareUrl && (
                <Button
                  asChild
                  size="icon-sm"
                  variant="ghost"
                  data-testid="share-mentions-link-preview"
                  onClick={() =>
                    postTrackingData(EVENT.SHARE_ALERT_CLICKED, {
                      shareUrl,
                      uuid: search?.uuid,
                    })
                  }
                >
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon />
                    <span className="sr-only">
                      <Trans>Öppna i en ny flik</Trans>
                    </span>
                  </a>
                </Button>
              )}

              <Button type="button" size="sm" onClick={handleCopyMentionsUrl}>
                <Trans>Kopiera</Trans>
              </Button>
            </ItemActions>
          </Item>

          <Item variant="outline" size="sm">
            <ItemMedia variant="icon">
              <ChartColumnBigIcon />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>
                <Trans>Publik länk för analys</Trans>
              </ItemTitle>
            </ItemContent>
            <ItemActions>
              {insightsShareUrl && (
                <Button
                  asChild
                  size="icon-sm"
                  variant="ghost"
                  data-testid="share-analytics-link-preview"
                  onClick={() =>
                    postTrackingData(EVENT.SHARE_ALERT_ANALYTICS_CLICKED, {
                      shareUrl: insightsShareUrl,
                      uuid: search?.uuid,
                    })
                  }
                >
                  <a
                    target="_blank"
                    href={insightsShareUrl}
                    rel="noopener noreferrer"
                  >
                    <ExternalLinkIcon />
                    <span className="sr-only">
                      <Trans>Öppna i en ny flik</Trans>
                    </span>
                  </a>
                </Button>
              )}

              <Button type="button" size="sm" onClick={handleCopyInsightsUrl}>
                <Trans>Kopiera</Trans>
              </Button>
            </ItemActions>
          </Item>

          <div className="flex flex-col gap-0 items-start">
            <Feature required={FEATURE.HAS_RSS_FEATURE}>
              {rssUrl && (
                <Button
                  asChild
                  size="sm"
                  variant="link"
                  onClick={() => {
                    postTrackingData(EVENT.SHARE_ALERT_CLICKED_RSS, {
                      rssUrl,
                    });
                  }}
                >
                  <a href={rssUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon />
                    <Trans>Länk till RSS</Trans>
                  </a>
                </Button>
              )}
            </Feature>

            <Feature required={FEATURE.HAS_JSON_API_FEATURE}>
              {jsonUrl && (
                <Button
                  asChild
                  size="sm"
                  variant="link"
                  onClick={() => {
                    postTrackingData(EVENT.SHARE_ALERT_CLICKED_API);
                  }}
                >
                  <a href={jsonUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon />
                    <Trans>Länk till API</Trans>
                  </a>
                </Button>
              )}
            </Feature>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
